import { useState } from "react";
import {
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from "@material-ui/core";
import {
  columnDateField,
  columnTextField,
  columnCNPJCPFField,
} from "utils/tables-utils";
import ToolkitTable from "components/Cards/Tables/ToolkitTable";
import {
  Add,
  FileDownloadOutlined,
  UploadFile,
  VisibilityOutlined,
} from "@mui/icons-material";
import verifyProfilesVision from "utils/verifyProfilesVision";
import MaskAndValid from "utils/maskAndValid";

function DocumentDownloadMenu({ documentos }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const docsPendentes =
    documentos?.filter((doc) => doc.StatusValidacao === "PENDENTE") ?? [];

  if (docsPendentes.length === 0) return null;

  const handleClose = () => setAnchorEl(null);

  const handleDownload = (url) => {
    if (url) window.open(url, "_blank");
    handleClose();
  };

  return (
    <>
      <Tooltip title="Baixar documentos">
        <IconButton
          onClick={(e) => setAnchorEl(e.currentTarget)}
          aria-controls={anchorEl ? "menu-download-docs" : undefined}
          aria-haspopup="true"
          aria-expanded={anchorEl ? "true" : undefined}
        >
          <FileDownloadOutlined />
        </IconButton>
      </Tooltip>
      <Menu
        id="menu-download-docs"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        style={{ marginTop: 40 }}
      >
        {docsPendentes.map((doc, index) => (
          <MenuItem
            key={doc.IdDocumento ?? doc.UrlArquivo ?? index}
            onClick={() => handleDownload(doc.UrlArquivo)}
          >
            {doc.TipoDocumento || `Documento ${index + 1}`}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export const LONG_STATUS_SOLICITACAO = {
  ABERTA: "Aberta",
  APROVADA: "Aprovada",
  REPROVADO: "Reprovada",
  AG_ENVIO_NOTA: "Ag Documentos",
  DOC_EM_VALIDACAO: "Doc em Validação",
  DOC_APROVADO: "Documentos Aprovados",
  DOC_REPROVADO: "Documentos Reprovados",
  CONCLUIDA: "Concluída",
};

export default function TableRequests({
  data,
  onView,
  onNewRequest,
  onTableChange,
  totalRegistros,
  currentPage,
  currentSizePerPage,
  onDownloadDocuments,
}) {
  const isADM = verifyProfilesVision(["CRC", "ADM"]);

  const columnModel = [
    columnTextField({
      dataField: "IdSolicitacao",
      text: "Código Solicitacao",
      align: "center",
    }),
    columnTextField({
      dataField: "Status",
      text: "Status",
      align: "center",
      formatter: (_, row) => {
        const status = row.StatusSolicitacao?.toUpperCase();
        const isOpen = status === "ABERTA";
        const isApproved = status === "APROVADA";
        const isRejected = status === "REPROVADO";
        const isWaitingDocuments = status === "AG_ENVIO_NOTA";
        const isDocValidation = status === "DOC_EM_VALIDACAO";
        const isDocApproved = status === "DOC_APROVADO";
        const isDocReproved = status === "DOC_REPROVADO";
        const isCompleted = status === "CONCLUIDA";

        let backgroundColor, borderColor;

        if (isApproved || isCompleted) {
          backgroundColor = "rgba(0, 128, 0, 0.1)";
          borderColor = "1px solid rgba(0, 128, 0, 0.5)";
        } else if (isOpen || isDocApproved) {
          backgroundColor = "rgba(255, 193, 7, 0.1)";
          borderColor = "1px solid rgba(255, 193, 7, 0.5)";
        } else if (isWaitingDocuments || isDocValidation) {
          backgroundColor = "rgba(33, 150, 243, 0.1)";
          borderColor = "1px solid rgba(33, 150, 243, 0.5)";
        } else if (isRejected || isDocReproved) {
          backgroundColor = "rgba(244, 67, 54, 0.1)";
          borderColor = "1px solid rgba(244, 67, 54, 0.5)";
        } else {
          backgroundColor = "rgba(128, 128, 128, 0.1)";
          borderColor = "1px solid rgba(128, 128, 128, 0.5)";
        }

        return (
          <Chip
            label={LONG_STATUS_SOLICITACAO[status]}
            style={{
              width: "150px",
              padding: "1rem 0.5rem",
              backgroundColor: backgroundColor,
              border: borderColor,
            }}
            size="small"
          />
        );
      },
    }),
    columnCNPJCPFField({
      dataField: "CNPJ",
      text: "CNPJ",
      align: "center",
    }),
    columnTextField({
      dataField: "Empresa",
      text: "Loja",
      align: "center",
    }),
    columnDateField({
      dataField: "DataCriado",
      text: "Data Solicitação",
      align: "center",
      formatter: (cell, _) => {
        return cell ? MaskAndValid.formattDateBR(cell) : "";
      },
    }),
    columnTextField({
      dataField: "Solicitante",
      text: "Solicitante",
      align: "center",
    }),
    columnTextField({
      dataField: "Motivo",
      text: "Motivo Devolução",
      align: "center",
    }),
    columnTextField({
      dataField: "Defeito",
      text: "Defeito",
      align: "center",
    }),
    columnTextField({
      dataField: "Ações",
      text: "Ações",
      align: "center",
      formatter: (_, row) => {
        const isWaitingDocuments = row.StatusSolicitacao === "AG_ENVIO_NOTA";
        const isDocValidation = row.StatusSolicitacao === "DOC_EM_VALIDACAO";
        return (
          <>
            <Tooltip title="Vizualizar Solicitação">
              <IconButton
                onClick={() => {
                  onView(row);
                }}
              >
                <VisibilityOutlined />
              </IconButton>
            </Tooltip>
            {!isADM && isWaitingDocuments && (
              <Tooltip title="Download de documentos">
                <IconButton
                  onClick={() => {
                    onDownloadDocuments(row.IdSolicitacao);
                  }}
                >
                  <UploadFile />
                </IconButton>
              </Tooltip>
            )}
            {isADM && isDocValidation && (
              <DocumentDownloadMenu documentos={row.Documentos} />
            )}
          </>
        );
      },
    }),
  ];

  return (
    <ToolkitTable
      dataTable={data}
      keyField={"IdSolicitacao"}
      columnModel={columnModel}
      bootstrapTableProps={{
        remote: true,
        onTableChange: onTableChange,
      }}
      headerTitle="Solicitações de Devolução"
      pagination={{
        options: [25, 50, 100],
        totalSize: totalRegistros,
        page: currentPage + 1,
        sizePerPage: currentSizePerPage,
        defaultOption: 25,
        showTotal: true,
      }}
      notFoundTitle="Sua pesquisa não encontrou nenhum resultado correspondente."
      extraActions={
        <Button variant="contained" color="primary" onClick={onNewRequest}>
          <Add />
          Nova Solicitação
        </Button>
      }
    />
  );
}
